import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { InputNumber, Label, Radio, Select, Text } from '@mercai/clever'

import type { PaymentFormProps } from './payment-form.type'
import {
  FinanceProfilePaymentFormsEnum,
  FinanceProfileTransactionPaymentMethodsEnum,
} from '@/types'
import styles from './payment-form.module.css'

export const PaymentForm = ({
  onChangeValues,
  values,
  financeProfileData,
  errors,
}: PaymentFormProps) => {
  const { t: tEnums } = useTranslation('enums')
  const { t } = useTranslation('screenQuotationV2List')

  const onHandleValues = (key: string, value: string | number) => {
    onChangeValues({ ...values, [key]: value })
  }

  const handleChangePaymentRates = (value: string) => {
    const availableRates = financeProfileData?.creditInterestRates
    const rate = availableRates?.[value] || ''
    const newValues = {
      ...values,
      creditTerm: value,
      financingCost: rate,
    }
    onChangeValues(newValues)
  }

  const handleChangePaymentForm = (
    value: keyof typeof FinanceProfilePaymentFormsEnum,
  ) => {
    if (FinanceProfilePaymentFormsEnum.credit === value) {
      const newValues = {
        ...values,
        paymentFormOption: value,
        purchaseOnCredit: true,
      }
      onChangeValues(newValues)
    } else {
      const newValues = {
        ...values,
        paymentFormOption: value,
        purchaseOnCredit: false,
        creditTerm: undefined,
        financingCost: undefined,
      }

      onChangeValues(newValues)
    }
  }

  const paymentFormDescriptions: Record<
    keyof typeof FinanceProfilePaymentFormsEnum,
    string
  > = {
    upfront: t('quotationFormPaymentFormUpFrontDescription'),
    partial: t('quotationFormPaymentFormPartialDescription', {
      value:
        (financeProfileData?.upfrontPaymentPercentage
          ? +financeProfileData.upfrontPaymentPercentage
          : 0) * 100,
      value2:
        100 -
        (financeProfileData?.upfrontPaymentPercentage
          ? +financeProfileData.upfrontPaymentPercentage
          : 0) *
        100,
    }),
    on_delivery: t('quotationFormPaymentFormOnDeliveryDescription'),
    credit: t('quotationFormPaymentFormInternalCreditDescription'),
  }

  const getPaymentForms = (
    key: keyof typeof FinanceProfilePaymentFormsEnum,
  ) => {
    return (
      <Radio
        key={key}
        value={values?.paymentFormOption === key}
        onChange={() => handleChangePaymentForm(key)}
      >
        <div className={styles['radio-label']}>
          <p className={styles['radio-label-title']}>
            {tEnums(`financial_profile_payment_forms_${key}`)}
          </p>
          <p>{paymentFormDescriptions[key]}</p>
        </div>
      </Radio>
    )
  }

  const getPaymentMethods = (
    key: keyof typeof FinanceProfileTransactionPaymentMethodsEnum,
  ) => {
    return (
      <Radio
        key={key}
        value={values?.paymentMethodOption === key}
        onChange={() => onHandleValues('paymentMethodOption', key)}
      >
        <div className={styles['radio-label']}>
          <p className={styles['radio-label-title']}>
            {tEnums(`financial_profile_transaction_payment_methods_${key}`)}
          </p>
        </div>
      </Radio>
    )
  }

  const ratesOptions = useMemo(() => {
    if (financeProfileData?.creditInterestRates) {
      const days = Object.keys(financeProfileData?.creditInterestRates)

      return days.map((day) => ({
        label: `${day} ${t('quotationFormPaymentFormPaymentsRatesDaysLabel')}`,
        value: day,
      }))
    }
    return []
  }, [financeProfileData])

  const ratePercentageValue = Number(values?.financingCost || 0) * 100

  return (
    <div className={styles.core}>
      <section>
        <div className={styles['section-title']}>
          <Text variant="text-large-bold">
            {t('quotationFormPaymentFormConditionsTitle')}
            <span className={styles.required}>*</span>
          </Text>
          <Text variant="text-medium">
            {t('quotationFormPaymentFormConditionsSubtitle')}
          </Text>
        </div>
        <div className={styles['radios-box']}>
          {financeProfileData?.enabledPaymentForms.map((value) =>
            getPaymentForms(value),
          )}
        </div>
        {errors.paymentFormOption && (
          <Text className={styles.error}>
            {errors.paymentFormOption as string}{' '}
          </Text>
        )}
      </section>

      <section>
        <div className={styles['section-title']}>
          <Text variant="text-large-bold">
            {t('quotationFormPaymentFormPaymentMethodsTitle')}
            <span className={styles.required}>*</span>
          </Text>
          <Text variant="text-medium">
            {t('quotationFormPaymentFormPaymentMethodsSubtitle')}
          </Text>
        </div>
        <div className={styles['radios-box']}>
          {financeProfileData?.enabledPaymentMethods.map((value) =>
            getPaymentMethods(value),
          )}
        </div>
        {errors.paymentMethodOption && (
          <Text className={styles.error}>
            {errors.paymentMethodOption as string}{' '}
          </Text>
        )}
      </section>

      {FinanceProfilePaymentFormsEnum.credit === values?.paymentFormOption && (
        <section>
          <div>
            <Label className={styles.label} required>
              {t('quotationFormPaymentFormCreditTermTitle')}
            </Label>
            <Select
              className={styles.select}
              value={values?.creditTerm || ''}
              onChange={handleChangePaymentRates}
              options={ratesOptions}
              placeholder={t('quotationFormPaymentFormSelectPlaceholder')}
              error={errors?.creditTerm as string}
            />
          </div>

          <div>
            <Label className={styles.label} required>
              {t('quotationFormPaymentFormFinancingCostTitle')}
            </Label>
            <InputNumber
              decimalPrecision={2}
              suffix="%"
              value={JSON.stringify(ratePercentageValue)}
              stepSize={1}
              min={0}
              showIcons
              onChange={(value) => {
                const parseToDecimal = Number(value) / 100
                onHandleValues('financingCost', JSON.stringify(parseToDecimal))
              }}
              error={errors?.financingCost as string}
              disabled
            />
          </div>
        </section>
      )}
    </div>
  )
}
